import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../../services/api';

interface DashboardStats {
  totalChats: number;
  totalDocuments: number;
  activeTags: number;
  runningAgents: number;
  emailsProcessedToday: number;
}

const INITIAL: DashboardStats = {
  totalChats: 0,
  totalDocuments: 0,
  activeTags: 0,
  runningAgents: 0,
  emailsProcessedToday: 0,
};

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats>(INITIAL);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [chats, docs, tags, agents] = await Promise.allSettled([
          get<any[]>('/api/chat/history'),
          get<any[]>('/api/documents'),
          get<any[]>('/api/tags?is_active=true'),
          get<any[]>('/api/email-agents'),
        ]);

        if (cancelled) return;

        const totalChats =
          chats.status === 'fulfilled'
            ? (chats.value as { total?: number; items?: unknown[] })?.total ??
              (Array.isArray(chats.value) ? chats.value.length : 0)
            : 0;
        const totalDocuments = docs.status === 'fulfilled' && Array.isArray(docs.value) ? docs.value.length : 0;
        const activeTags = tags.status === 'fulfilled' && Array.isArray(tags.value) ? tags.value.length : 0;

        let runningAgents = 0;
        let emailsProcessedToday = 0;
        if (agents.status === 'fulfilled' && Array.isArray(agents.value)) {
          runningAgents = agents.value.filter((a: any) => a.status === 'running').length;
          emailsProcessedToday = agents.value.reduce(
            (sum: number, a: any) => sum + (a.processed_today ?? 0),
            0,
          );
        }

        setStats({ totalChats, totalDocuments, activeTags, runningAgents, emailsProcessedToday });
      } catch {
        // keep initial zeros on error
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  const safeNum = (n: unknown): number => (typeof n === 'number' && !Number.isNaN(n) ? n : 0);

  const cards: { title: string; value: number; label: string; link: string; color: string }[] = [
    { title: 'Query Engine', value: safeNum(stats.totalChats), label: 'total chats', link: '/query', color: 'bg-blue-500' },
    { title: 'Knowledge Library', value: safeNum(stats.totalDocuments), label: 'documents ingested', link: '/knowledge', color: 'bg-emerald-500' },
    { title: 'Active Tags', value: safeNum(stats.activeTags), label: 'tags configured', link: '/tags', color: 'bg-violet-500' },
    { title: 'Email Agents', value: safeNum(stats.runningAgents), label: 'agents running', link: '/email-agents', color: 'bg-amber-500' },
    { title: 'Emails Today', value: safeNum(stats.emailsProcessedToday), label: 'processed', link: '/email-agents', color: 'bg-rose-500' },
  ];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-1">Dashboard</h1>
      <p className="text-gray-500 mb-8">DDQ Master &mdash; unified overview</p>

      {loading ? (
        <div className="flex items-center justify-center h-48 text-gray-400">Loading&hellip;</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((c) => (
            <Link
              key={c.title}
              to={c.link}
              className="group rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-3 h-3 rounded-full ${c.color}`} />
                <span className="text-sm font-medium text-gray-500 group-hover:text-gray-700 transition-colors">
                  {c.title}
                </span>
              </div>
              <p className="text-3xl font-bold text-gray-800">{safeNum(c.value).toLocaleString()}</p>
              <p className="text-sm text-gray-400 mt-1">{c.label}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
